import {
  ApiHolder,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import type {
  ConversationAssistant,
  Message,
  Conversation,
  ChatCompletionResponse,
  ChatAgentRequest,
  ChatAgentResponse,
  AgentToolCallTrace,
} from './MgaApi';
import { LocalConversationStorage } from './LocalConversationStorage';
import {
  BackstageAgentStatus,
  BackstageAgentStreamEventType,
  BACKSTAGE_AGENT_MAX_HISTORY_TURNS,
  CHAT_COMPLETION_OBJECT,
  MessageRole,
  STORAGE_KEYS,
} from './constants';
import idpAgentLogo from '../assets/idp-agent-logo.png';

export const BACKSTAGE_AGENT_ASSISTANT_ID = 'backstage-agent';

export const BACKSTAGE_AGENT_ASSISTANT_INFO: ConversationAssistant = {
  id: BACKSTAGE_AGENT_ASSISTANT_ID,
  name: 'IDP Agent',
  description:
    'Agentic assistant for live Backstage catalog, TechDocs, scorecards, and tech radar questions.',
  logoUrl: idpAgentLogo,
  model: 'IDP Agent',
  topic: 'Developer Portal',
  instructions:
    'Use live Backstage tools to answer questions about catalog entities, documentation, scorecards, and tech radar.',
};

interface BackstageAgentChatRequest {
  message: string;
  history?: Array<{
    role: typeof MessageRole.USER | typeof MessageRole.ASSISTANT;
    content: string;
  }>;
  sessionId?: string;
  preset?: 'chat';
}

interface BackstageAgentResponse {
  answer: string;
  steps: number;
  llmTurns: number;
  status: (typeof BackstageAgentStatus)[keyof typeof BackstageAgentStatus];
  toolCalls: AgentToolCallTrace[];
  sessionId?: string;
  errorMessage?: string;
}

type BackstageAgentStreamEvent =
  | {
      type: typeof BackstageAgentStreamEventType.ROUTING;
      intents: Array<{ text: string; domain: string }>;
      timestamp: string;
    }
  | {
      type: typeof BackstageAgentStreamEventType.SUB_AGENT_START;
      domain: string;
      intent: string;
      timestamp: string;
    }
  | {
      type: typeof BackstageAgentStreamEventType.SUB_AGENT_COMPLETE;
      domain: string;
      intent: string;
      answer: string;
      toolCalls: AgentToolCallTrace[];
      isError: boolean;
      timestamp: string;
    }
  | {
      type: typeof BackstageAgentStreamEventType.SYNTHESIZING;
      subRunCount: number;
      timestamp: string;
    }
  | {
      type: typeof BackstageAgentStreamEventType.COMPLETE;
      data: BackstageAgentResponse;
      timestamp: string;
    }
  | {
      type: typeof BackstageAgentStreamEventType.ERROR;
      message: string;
      timestamp: string;
    }
  | {
      type: typeof BackstageAgentStreamEventType.CANCELLED;
      message: string;
      timestamp: string;
    };

interface SseMessage {
  event?: string;
  data: string;
}

export interface BackstageAgentMessage {
  id: string;
  role: typeof MessageRole.USER | typeof MessageRole.ASSISTANT;
  content: string;
  timestamp: string;
  cost?: number;
  agentToolCalls?: AgentToolCallTrace[];
  agentProgress?: string[];
}

export interface BackstageAgentConversation {
  id: string;
  messages: BackstageAgentMessage[];
  title: string;
  timestamp: string;
  sessionId?: string;
}

function createAssistantMessage(
  content: string,
  toolCalls: AgentToolCallTrace[],
  progress: string[],
): BackstageAgentMessage {
  return {
    id: `msg-${Date.now()}-assistant`,
    role: MessageRole.ASSISTANT,
    content,
    timestamp: new Date().toISOString(),
    cost: 0,
    agentToolCalls: toolCalls,
    agentProgress: progress,
  };
}

function getProgressText(event: BackstageAgentStreamEvent): string | undefined {
  switch (event.type) {
    case BackstageAgentStreamEventType.ROUTING:
      return event.intents.length > 0
        ? `Routing to ${event.intents
            .map(intent => intent.domain)
            .join(', ')}...`
        : 'Routing request...';
    case BackstageAgentStreamEventType.SUB_AGENT_START:
      return `Checking ${event.domain}...`;
    case BackstageAgentStreamEventType.SUB_AGENT_COMPLETE:
      return event.isError
        ? `${event.domain} check failed.`
        : `${event.domain} check complete.`;
    case BackstageAgentStreamEventType.SYNTHESIZING:
      return `Synthesizing ${event.subRunCount} results...`;
    default:
      return undefined;
  }
}

type SseChunk = Uint8Array | string | number;

function toUint8Array(chunk: SseChunk): Uint8Array {
  if (typeof chunk === 'number') {
    return Uint8Array.of(chunk);
  }
  if (typeof chunk === 'string') {
    return new TextEncoder().encode(chunk);
  }
  return chunk;
}

async function* readStreamChunks(
  body: ReadableStream<Uint8Array> | AsyncIterable<SseChunk>,
): AsyncGenerator<Uint8Array> {
  if ('getReader' in body && typeof body.getReader === 'function') {
    const reader = body.getReader();
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) yield value;
      }
    } finally {
      reader.releaseLock();
    }
    return;
  }

  const iterableBody = body as AsyncIterable<SseChunk>;
  for await (const chunk of iterableBody) {
    yield toUint8Array(chunk);
  }
}

async function* parseSseStream(
  body: ReadableStream<Uint8Array> | AsyncIterable<SseChunk>,
): AsyncGenerator<SseMessage> {
  const decoder = new TextDecoder();
  let buffer = '';

  for await (const value of readStreamChunks(body)) {
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split(/\r?\n\r?\n/);
    buffer = parts.pop() || '';

    for (const part of parts) {
      const lines = part.split(/\r?\n/);
      const data: string[] = [];
      let event: string | undefined;

      for (const line of lines) {
        if (line.startsWith('event:')) {
          event = line.slice('event:'.length).trim();
        } else if (line.startsWith('data:')) {
          data.push(line.slice('data:'.length).trimStart());
        }
      }

      if (data.length > 0) {
        yield { event, data: data.join('\n') };
      }
    }
  }
}

export class BackstageAgentAdapter {
  readonly conversationPrefix = 'agent';
  readonly assistantInfo = BACKSTAGE_AGENT_ASSISTANT_INFO;

  private apiHolder: ApiHolder;
  private conversationStorage =
    new LocalConversationStorage<BackstageAgentConversation>({
      conversation: STORAGE_KEYS.agentConversation,
      conversationList: STORAGE_KEYS.AGENT_CONVERSATION_LIST,
    });

  constructor(apiHolder: ApiHolder) {
    this.apiHolder = apiHolder;
  }

  private getDiscoveryApi() {
    return this.apiHolder.get(discoveryApiRef);
  }

  private getFetchApi() {
    return this.apiHolder.get(fetchApiRef);
  }

  getConversation(conversationId: string): BackstageAgentConversation | null {
    return this.conversationStorage.get(conversationId);
  }

  async sendMessage(
    request: ChatAgentRequest,
    options?: {
      onStream?: (response: ChatCompletionResponse) => void;
      ephemeral?: boolean;
    },
  ): Promise<ChatAgentResponse> {
    const discoveryApi = this.getDiscoveryApi();
    const fetchApi = this.getFetchApi();

    if (!discoveryApi || !fetchApi) {
      throw new Error('Backstage Agent API dependencies are not available');
    }

    const userMessage = request.messages[request.messages.length - 1];
    if (!userMessage || userMessage.role !== MessageRole.USER) {
      throw new Error('No user message found in request');
    }

    const conversationId =
      request.conversation_id || `${this.conversationPrefix}-${Date.now()}`;
    let conversation = this.getConversation(conversationId);
    if (!conversation) {
      conversation = {
        id: conversationId,
        messages: [],
        title: userMessage.content.substring(0, 50),
        timestamp: new Date().toISOString(),
      };
    }

    const history = request.messages
      .slice(0, -1)
      .filter(
        message =>
          message.role === MessageRole.USER ||
          message.role === MessageRole.ASSISTANT,
      )
      .map(message => ({
        role: message.role as
          | typeof MessageRole.USER
          | typeof MessageRole.ASSISTANT,
        content: message.content,
      }))
      .slice(-BACKSTAGE_AGENT_MAX_HISTORY_TURNS);

    const agentRequest: BackstageAgentChatRequest = {
      message: userMessage.content,
      history,
      sessionId: conversation.sessionId,
      preset: 'chat',
    };

    const userAgentMessage: BackstageAgentMessage = {
      id: `msg-${Date.now()}-user`,
      role: MessageRole.USER,
      content: userMessage.content,
      timestamp: new Date().toISOString(),
      cost: 0,
    };
    conversation.messages.push(userAgentMessage);

    const progress: string[] = [];
    let finalResponse: BackstageAgentResponse | undefined;
    let collectedToolCalls: AgentToolCallTrace[] = [];

    try {
      const baseUrl = await discoveryApi.getBaseUrl('backstage-agent');
      const response = await fetchApi.fetch(`${baseUrl}/v1/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(agentRequest),
      });

      if (!response.ok || !response.body) {
        const errorText = await response.text();
        throw new Error(
          errorText ||
            `Backstage Agent request failed: ${response.status} ${response.statusText}`,
        );
      }

      for await (const message of parseSseStream(response.body)) {
        const event = JSON.parse(message.data) as BackstageAgentStreamEvent;
        const progressText = getProgressText(event);

        if (progressText) {
          progress.push(progressText);
          options?.onStream?.({
            conversationId,
            model: BACKSTAGE_AGENT_ASSISTANT_INFO.model || 'IDP Agent',
            content: progressText,
          });
        }

        if (event.type === BackstageAgentStreamEventType.SUB_AGENT_COMPLETE) {
          collectedToolCalls = collectedToolCalls.concat(event.toolCalls);
        }

        if (event.type === BackstageAgentStreamEventType.COMPLETE) {
          finalResponse = event.data;
        }

        if (
          event.type === BackstageAgentStreamEventType.ERROR ||
          event.type === BackstageAgentStreamEventType.CANCELLED
        ) {
          throw new Error(event.message);
        }
      }

      if (!finalResponse) {
        throw new Error(
          'Backstage Agent stream ended without a final response',
        );
      }

      if (
        finalResponse.status === BackstageAgentStatus.ERROR ||
        finalResponse.status === BackstageAgentStatus.TIMEOUT ||
        finalResponse.status === BackstageAgentStatus.CANCELLED
      ) {
        throw new Error(
          finalResponse.errorMessage ||
            `Backstage Agent finished with status ${finalResponse.status}`,
        );
      }

      const toolCalls =
        finalResponse.toolCalls.length > 0
          ? finalResponse.toolCalls
          : collectedToolCalls;
      const assistantMessage = createAssistantMessage(
        finalResponse.answer,
        toolCalls,
        progress,
      );
      conversation.messages.push(assistantMessage);
      conversation.timestamp = new Date().toISOString();
      conversation.sessionId =
        finalResponse.sessionId || conversation.sessionId;
      this.conversationStorage.save(conversation, options);

      return {
        id: conversationId,
        parent_id: null,
        object: CHAT_COMPLETION_OBJECT,
        created: Date.now(),
        model: BACKSTAGE_AGENT_ASSISTANT_INFO.model || 'IDP Agent',
        system_fingerprint: null,
        usage: {
          prompt_tokens: userMessage.content.length,
          completion_tokens: finalResponse.answer.length,
          total_tokens:
            userMessage.content.length + finalResponse.answer.length,
        },
        choices: [
          {
            message: {
              role: MessageRole.ASSISTANT,
              content: finalResponse.answer,
            },
            finish_reason: 'stop',
            index: 0,
          },
        ],
        prompt_annotations: null,
        cost: 0,
        conversation_id: conversationId,
        metadata: [
          {
            cost: { total_cost: 0 },
            choices: [],
          },
        ],
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An unknown error occurred';
      conversation.messages.push(
        createAssistantMessage(
          `Error: ${errorMessage}`,
          [],
          [...progress, 'IDP Agent failed.'],
        ),
      );
      conversation.timestamp = new Date().toISOString();
      this.conversationStorage.save(conversation, options);
      throw error;
    }
  }

  convertToMgaConversation(
    agentConversation: BackstageAgentConversation,
  ): Conversation {
    const messages: Message[] = agentConversation.messages.map(msg => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      cost: msg.cost || 0,
      creation_datetime: msg.timestamp,
      conversation_id: agentConversation.id,
      parent_id: null,
      ...(msg.agentToolCalls && { agentToolCalls: msg.agentToolCalls }),
      ...(msg.agentProgress && { agentProgress: msg.agentProgress }),
    }));

    return {
      id: agentConversation.id,
      messages,
      assistant: {
        id: BACKSTAGE_AGENT_ASSISTANT_ID,
        name: BACKSTAGE_AGENT_ASSISTANT_INFO.name,
        logo_url: BACKSTAGE_AGENT_ASSISTANT_INFO.logoUrl || '',
        description: BACKSTAGE_AGENT_ASSISTANT_INFO.description || '',
        model: {
          model: BACKSTAGE_AGENT_ASSISTANT_INFO.model || '',
          name: BACKSTAGE_AGENT_ASSISTANT_INFO.model || '',
        },
        tools: [],
        files: [],
      },
      name: agentConversation.title,
      modification_date: agentConversation.timestamp,
      total_cost: messages.reduce((sum, msg) => sum + (msg.cost || 0), 0),
      tools: [],
      files: [],
    };
  }

  getAllConversations(): BackstageAgentConversation[] {
    return this.conversationStorage.getAll();
  }

  deleteConversation(conversationId: string): void {
    this.conversationStorage.delete(conversationId);
  }

  updateConversationTitle(conversationId: string, title: string): void {
    const conversation = this.getConversation(conversationId);
    if (!conversation) {
      throw new Error(`Backstage Agent conversation ${conversationId} not found`);
    }

    conversation.title = title;
    conversation.timestamp = new Date().toISOString();
    this.conversationStorage.save(conversation);
  }
}
